import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { ApiClient } from '../api'
import { useMe } from '../hooks/useMe'
import { useEventTypes } from '../hooks/useEvents'
import { formatGBP } from '../lib/money'
import type { ReportsFilters, EventStatus, ReportsPayload } from '../types'

// Reports - cross-event KPI summary + simple SVG charts + CSV export.
// Uses inline SVG rather than pulling in recharts so we ship without a
// new dependency. If chart needs grow (interactivity, tooltips, etc.),
// swap to recharts behind these same chart components.

const STATUSES: EventStatus[] = ['Draft', 'In Development', 'Live', 'Complete', 'Cancelled']

function defaultFilters(): ReportsFilters {
  const today = new Date()
  const fromDate = `${today.getFullYear()}-01-01`
  const toDate = `${today.getFullYear()}-12-31`
  return { fromDate, toDate, types: [], status: [] }
}

export function Reports() {
  const { data: me } = useMe()
  const { data: types } = useEventTypes()
  const [filters, setFilters] = useState<ReportsFilters>(defaultFilters)
  const { data, isLoading, error } = useQuery({
    queryKey: ['reports', filters],
    queryFn: () => ApiClient.reportsRollup(filters)
  })

  const monthMax = useMemo(() => {
    return Math.max(1, ...(data?.byMonth.map((m) => m.count) ?? []))
  }, [data])

  function exportCsv() {
    if (!data) return
    const lines = ['section,key,value']
    lines.push(`kpi,events,${data.kpis.events}`)
    lines.push(`kpi,attendees,${data.kpis.attendees}`)
    lines.push(`kpi,revenue,${data.kpis.revenue}`)
    lines.push(`kpi,volunteerHours,${data.kpis.volunteerHours}`)
    for (const m of data.byMonth) lines.push(`byMonth,${m.month},${m.count}`)
    for (const a of data.attendance) lines.push(`attendance,${a.eventId} ${csvEscape(a.name)},${a.capacity}`)
    for (const p of data.progress) lines.push(`progress,${p.bucket},${p.count}`)
    for (const v of data.variance) lines.push(`variance,${v.eventId} ${csvEscape(v.name)},${v.variancePct ?? ''}`)
    // MERAC revenue export — money in pence as on the wire so the CSV
    // round-trips losslessly. Recipients can /100 in their tool.
    lines.push(`merac_total,booked_pence,${data.meracRevenue.totalGrossPence}`)
    lines.push(`merac_total,gate_pence,${data.meracRevenue.totalGatePence}`)
    lines.push(`merac_total,total_pence,${data.meracRevenue.totalPence}`)
    lines.push(`merac_total,net_pence,${data.meracRevenue.totalNetPence}`)
    lines.push(`merac_total,sessions,${data.meracRevenue.totalSessions}`)
    for (const r of data.meracRevenue.byEvent) {
      lines.push(`merac_by_event,${r.eventId} ${csvEscape(r.name)},booked_pence=${r.grossPence};gate_pence=${r.gatePence};net_pence=${r.netPence};sessions=${r.sessionCount};last=${r.lastSessionAt ?? ''}`)
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `nymr-rollup-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl mb-1">Reports</h1>
          <p className="text-ink-soft">
            Cross-event rollup. Filter the date range, type, and status to drill in.
          </p>
        </div>
        <button
          type="button"
          onClick={exportCsv}
          disabled={!data}
          className="px-4 py-2 text-sm font-semibold rounded border border-line text-ink-soft hover:text-green hover:border-green transition-colors disabled:opacity-50"
        >
          Export CSV
        </button>
      </div>

      {/* Global documents — browser-print PDFs across all events. */}
      <div className="bg-white border border-line rounded p-4">
        <div className="text-xs uppercase tracking-wide font-semibold text-ink-soft mb-2">All-events documents</div>
        <div className="flex flex-wrap gap-2">
          <a
            href="/catering-report"
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-1.5 text-sm font-semibold rounded border border-line text-ink-soft hover:text-green hover:border-green transition-colors"
          >
            Catering requirements (PDF)
          </a>
          <a
            href="/service-timetable-report"
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-1.5 text-sm font-semibold rounded border border-line text-ink-soft hover:text-green hover:border-green transition-colors"
          >
            Event service timetables (PDF)
          </a>
        </div>
      </div>

      {/* Filter bar */}
      <div className="bg-white border border-line rounded p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <label className="block">
          <span className="text-xs uppercase tracking-wide font-semibold text-ink-soft block mb-1">
            From
          </span>
          <input
            type="date"
            value={filters.fromDate ?? ''}
            onChange={(e) => setFilters({ ...filters, fromDate: e.target.value || null })}
            className="w-full border border-line rounded px-2.5 py-1.5 text-sm"
          />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-wide font-semibold text-ink-soft block mb-1">
            To
          </span>
          <input
            type="date"
            value={filters.toDate ?? ''}
            onChange={(e) => setFilters({ ...filters, toDate: e.target.value || null })}
            className="w-full border border-line rounded px-2.5 py-1.5 text-sm"
          />
        </label>
        <div className="md:col-span-2">
          <span className="text-xs uppercase tracking-wide font-semibold text-ink-soft block mb-1">
            Types &amp; statuses
          </span>
          <div className="flex flex-wrap gap-1.5">
            {(types ?? []).map((t) => {
              const active = filters.types.includes(t.name)
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() =>
                    setFilters({
                      ...filters,
                      types: active ? filters.types.filter((x) => x !== t.name) : [...filters.types, t.name]
                    })
                  }
                  className="px-2.5 py-1 text-xs font-semibold rounded-full border transition-colors"
                  style={
                    active
                      ? { background: t.colour_bg, color: t.colour_text, borderColor: t.colour_border }
                      : { background: 'transparent', borderColor: '#d8d2c4', color: '#5a5a5a' }
                  }
                >
                  {t.name}
                </button>
              )
            })}
            {STATUSES.map((s) => {
              const active = filters.status.includes(s)
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() =>
                    setFilters({
                      ...filters,
                      status: active ? filters.status.filter((x) => x !== s) : [...filters.status, s]
                    })
                  }
                  className={
                    'px-2.5 py-1 text-xs font-semibold rounded-full border transition-colors ' +
                    (active ? 'bg-green text-parchment border-green' : 'bg-white text-ink-soft border-line')
                  }
                >
                  {s}
                </button>
              )
            })}
          </div>
        </div>
      </div>

      {isLoading && <div className="text-ink-soft text-sm font-mono">Loading rollup…</div>}

      {error && (
        <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-4 text-sm">
          <div className="font-semibold mb-1">Could not load report data</div>
          <pre className="text-xs whitespace-pre-wrap">{(error as Error).message}</pre>
        </div>
      )}

      {data && (
        <>
          {/* KPI strip */}
          <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
            <Kpi label="Events" value={data.kpis.events.toString()} accent="green" />
            <Kpi label="Attendees" value={data.kpis.attendees.toLocaleString('en-GB')} accent="slate" suffix={
              <span className="text-[10px] text-ink-soft">via Square (pending)</span>
            } />
            <Kpi
              label="Revenue"
              value={me?.hasFinancial ? `£${data.kpis.revenue.toLocaleString('en-GB', { maximumFractionDigits: 0 })}` : '—'}
              accent="brass"
            />
            <Kpi label="Volunteer hours" value={data.kpis.volunteerHours.toLocaleString('en-GB')} accent="oxblood" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <ChartCard title="Events by month">
              <BarChart data={data.byMonth.map((m) => ({ label: m.month, value: m.count }))} max={monthMax} colour="#0a4a3a" />
            </ChartCard>

            <ChartCard title="Build progress distribution">
              <BarChart
                data={data.progress.map((p) => ({ label: p.bucket, value: p.count }))}
                max={Math.max(1, ...data.progress.map((p) => p.count))}
                colour="#9b7f2a"
              />
            </ChartCard>

            <ChartCard title="Capacity by event">
              <AttendanceList rows={data.attendance.slice(0, 12)} />
            </ChartCard>

            {me?.hasFinancial && (
              <ChartCard title="Budget variance (vs baseline net)">
                <VarianceList rows={data.variance} />
              </ChartCard>
            )}
          </div>

          {/* MERAC ticket revenue — cross-event roll-up of MeracSessions
              joined via MeracEventMap. Financial-only; server suppresses
              the data when !hasFinancial so the empty totals + array
              shape is what non-finance users would see anyway. */}
          {me?.hasFinancial && <MeracRevenueCard data={data.meracRevenue} />}
        </>
      )}
    </div>
  )
}

// ---- MERAC ticket revenue card --------------------------------------------

function MeracRevenueCard({ data }: { data: ReportsPayload['meracRevenue'] }) {
  const { totalGrossPence, totalNetPence, totalGatePence, totalPence, totalSessions, byEvent } = data
  const empty = byEvent.length === 0

  // Most-recent activity stamp across all bound events. Useful as a
  // freshness signal — if it's hours/days old, the .ps1 on Ontrack
  // hasn't fired recently.
  const lastSessionAt = empty
    ? null
    : byEvent
        .map((e) => e.lastSessionAt)
        .filter((s): s is string => !!s)
        .sort()
        .pop() ?? null

  // Per-event gross is the bar value; max scales the bar widths.
  const maxGross = empty ? 0 : Math.max(...byEvent.map((e) => e.grossPence))

  return (
    <div className="bg-white border border-line rounded">
      <div className="px-4 py-3 border-b border-line flex items-baseline gap-3">
        <div className="flex-1">
          <h2 className="font-display text-lg">MERAC ticket revenue</h2>
          <p className="text-xs text-ink-soft mt-0.5">
            Booked (advance) from <code className="font-mono text-[11px]">dbo.MeracSessions</code> +
            gate (walk-up) from <code className="font-mono text-[11px]">dbo.MeracEventIncome</code>,
            for every event in the current filter. Total takings = booked + gate. Net is
            after refunds. Gate fills in once the daily gate/till sync has run for an event.
          </p>
        </div>
        {lastSessionAt && (
          <span className="text-[11px] text-ink-soft font-mono whitespace-nowrap">
            last session {formatLastSession(lastSessionAt)}
          </span>
        )}
      </div>

      {/* Totals strip — booked (advance) + gate (walk-up) + combined total */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-4 border-b border-line">
        <MeracStat label="Booked (advance)" value={formatGBP(totalGrossPence)} accent="slate" />
        <MeracStat label="Gate (walk-up)" value={formatGBP(totalGatePence)} accent="slate" />
        <MeracStat label="Total takings" value={formatGBP(totalPence)} accent="green" />
        <MeracStat
          label="Net (after refunds)"
          value={formatGBP(totalNetPence)}
          accent={totalNetPence >= 0 ? 'green' : 'oxblood'}
        />
        <MeracStat label="Sessions" value={totalSessions.toLocaleString('en-GB')} accent="slate" />
      </div>

      {empty ? (
        <div className="px-4 py-6 text-sm italic text-ink-soft">
          No MERAC sessions for the filtered events yet. Once events have
          a <code className="font-mono">MeracEventMap</code> row and the
          ontrack <code className="font-mono">.ps1</code> has fired, totals
          appear here.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wide text-ink-soft border-b border-line bg-parchment/60">
                <th className="px-4 py-2">Event</th>
                <th className="px-3 py-2 w-24 text-right">Sessions</th>
                <th className="px-3 py-2">Gross share</th>
                <th className="px-3 py-2 w-28 text-right">Gross</th>
                <th className="px-3 py-2 w-28 text-right">Net</th>
                <th className="px-3 py-2 w-32 whitespace-nowrap">Last session</th>
              </tr>
            </thead>
            <tbody>
              {byEvent.map((e) => (
                <tr
                  key={e.eventId}
                  className="border-b border-line last:border-b-0 hover:bg-parchment/40 transition-colors"
                >
                  <td className="px-4 py-2.5">
                    <Link
                      to={`/events/${e.eventId}`}
                      className="font-semibold hover:text-green hover:underline"
                    >
                      {e.name}
                    </Link>
                  </td>
                  <td className="px-3 py-2.5 text-right font-mono">
                    {e.sessionCount.toLocaleString('en-GB')}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="h-2 bg-parchment rounded overflow-hidden">
                      <div
                        className="h-full bg-green"
                        style={{ width: `${maxGross === 0 ? 0 : (e.grossPence / maxGross) * 100}%` }}
                      />
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-right font-mono font-semibold">
                    {formatGBP(e.grossPence)}
                  </td>
                  <td
                    className="px-3 py-2.5 text-right font-mono"
                    style={e.netPence < 0 ? { color: '#7a2329' } : undefined}
                  >
                    {formatGBP(e.netPence)}
                  </td>
                  <td className="px-3 py-2.5 text-[11px] text-ink-soft font-mono whitespace-nowrap">
                    {e.lastSessionAt ? formatLastSession(e.lastSessionAt) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function MeracStat({
  label, value, accent
}: {
  label: string
  value: string
  accent: 'green' | 'brass' | 'slate' | 'oxblood'
}) {
  const ACC = { green: '#0a4a3a', brass: '#9b7f2a', slate: '#3d5a6c', oxblood: '#7a2329' }
  return (
    <div className="relative bg-parchment/60 rounded" style={{ padding: '12px 14px' }}>
      <span
        aria-hidden
        className="absolute left-0 top-0 bottom-0"
        style={{ width: 3, background: ACC[accent] }}
      />
      <div className="text-[10px] uppercase tracking-wider font-semibold text-ink-soft mb-1">
        {label}
      </div>
      <div className="font-display text-xl font-bold leading-none">{value}</div>
    </div>
  )
}

// "23 Apr 14:22" — short relative-ish stamp for the freshness signal.
// Uses en-GB locale to match the rest of the app.
function formatLastSession(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const date = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
  return `${date} ${time}`
}

// ---- KPI card -------------------------------------------------------------

function Kpi({
  label, value, accent, suffix
}: {
  label: string
  value: string
  accent: 'green' | 'brass' | 'slate' | 'oxblood'
  suffix?: React.ReactNode
}) {
  const ACC = { green: '#0a4a3a', brass: '#9b7f2a', slate: '#3d5a6c', oxblood: '#7a2329' }
  return (
    <div className="relative bg-white border border-line rounded-lg overflow-hidden" style={{ padding: '18px 20px' }}>
      <span aria-hidden className="absolute left-0 top-0 bottom-0" style={{ width: 3, background: ACC[accent] }} />
      <div className="text-[11.5px] uppercase tracking-wider font-semibold text-ink-soft mb-1.5">{label}</div>
      <div className="font-display text-2xl font-bold leading-none">{value}</div>
      {suffix && <div className="mt-1">{suffix}</div>}
    </div>
  )
}

// ---- Chart card -----------------------------------------------------------

function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white border border-line rounded">
      <div className="px-4 py-3 border-b border-line">
        <h2 className="font-display text-lg">{title}</h2>
      </div>
      <div className="p-4">{children}</div>
    </div>
  )
}

// ---- Bar chart (inline SVG) ----------------------------------------------

function BarChart({
  data, max, colour
}: {
  data: Array<{ label: string; value: number }>
  max: number
  colour: string
}) {
  if (data.length === 0) {
    return <p className="text-xs italic text-ink-soft">No data for this filter.</p>
  }
  const W = 480, H = 200
  const padding = { l: 40, r: 12, t: 8, b: 32 }
  const innerW = W - padding.l - padding.r
  const innerH = H - padding.t - padding.b
  const barW = innerW / data.length

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label="bar chart">
      {/* y-axis label (max) */}
      <text x={padding.l - 6} y={padding.t + 10} textAnchor="end" fontSize="10" fill="#5a5a5a" fontFamily="monospace">
        {max}
      </text>
      <text x={padding.l - 6} y={padding.t + innerH} textAnchor="end" fontSize="10" fill="#5a5a5a" fontFamily="monospace">
        0
      </text>
      <line x1={padding.l} y1={padding.t} x2={padding.l} y2={padding.t + innerH} stroke="#d8d2c4" />
      <line x1={padding.l} y1={padding.t + innerH} x2={padding.l + innerW} y2={padding.t + innerH} stroke="#d8d2c4" />

      {data.map((d, i) => {
        const h = max === 0 ? 0 : (d.value / max) * innerH
        const x = padding.l + i * barW + barW * 0.15
        const y = padding.t + innerH - h
        const w = barW * 0.7
        return (
          <g key={d.label}>
            <rect x={x} y={y} width={w} height={h} fill={colour} rx={2}>
              <title>{`${d.label}: ${d.value}`}</title>
            </rect>
            <text
              x={x + w / 2}
              y={padding.t + innerH + 14}
              textAnchor="middle"
              fontSize="10"
              fill="#5a5a5a"
            >
              {d.label}
            </text>
            <text
              x={x + w / 2}
              y={y - 3}
              textAnchor="middle"
              fontSize="10"
              fill="#1a1a1a"
              fontFamily="monospace"
            >
              {d.value > 0 ? d.value : ''}
            </text>
          </g>
        )
      })}
    </svg>
  )
}

// ---- Attendance list ------------------------------------------------------

function AttendanceList({ rows }: { rows: Array<{ eventId: number; name: string; capacity: number; sold: number }> }) {
  if (rows.length === 0) return <p className="text-xs italic text-ink-soft">No events match the filter.</p>
  const maxCap = Math.max(1, ...rows.map((r) => r.capacity))
  return (
    <ul className="space-y-2">
      {rows.map((r) => (
        <li key={r.eventId} className="grid grid-cols-[1fr_4fr_auto] items-center gap-2 text-xs">
          <span className="truncate">{r.name}</span>
          <div className="h-2 bg-parchment rounded overflow-hidden">
            <div
              className="h-full bg-green"
              style={{ width: `${(r.capacity / maxCap) * 100}%` }}
            />
          </div>
          <span className="font-mono text-ink-soft whitespace-nowrap">{r.capacity.toLocaleString('en-GB')}</span>
        </li>
      ))}
    </ul>
  )
}

// ---- Variance list --------------------------------------------------------

function VarianceList({ rows }: { rows: Array<{ eventId: number; name: string; variancePct: number | null }> }) {
  const filtered = rows.filter((r) => r.variancePct != null)
  if (filtered.length === 0) {
    return <p className="text-xs italic text-ink-soft">No baselined budgets in this filter.</p>
  }
  return (
    <ul className="space-y-2 text-xs">
      {filtered.slice(0, 12).map((r) => {
        const pct = r.variancePct!
        const colours = pct >= 0 ? '#dcebe5,#0a4a3a' : pct >= -10 ? '#f5edd5,#9b7f2a' : '#f8dbdd,#7a2329'
        const [bg, fg] = colours.split(',')
        return (
          <li key={r.eventId} className="flex items-center gap-2">
            <span className="truncate flex-1">{r.name}</span>
            <span className="font-mono px-1.5 py-0.5 rounded" style={{ background: bg, color: fg }}>
              {pct >= 0 ? '+' : ''}{pct.toFixed(1)}%
            </span>
          </li>
        )
      })}
    </ul>
  )
}

function csvEscape(s: string): string {
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`
  }
  return s
}
